import omit from 'lodash/omit.js'
import {
  formsSelector,
  getState,
  onlyIfForm,
  removeForm,
  setForm,
  updateForm,
} from '../../../store.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import apiverson from '../../../common/apiverson.js'
import {buildPath} from '../../../common/querystring.js'
import {currentPageSelector} from '../../ReturnOrderListPage/returnOrderListSelectors.js'
import {STOCKTAKE_LIST_PAGE} from '../../../common/constants/Pages.js'
import {refreshStocktakeList} from '../stocktakeListFunctions.js'
import {refreshStocktakeDetails} from '../../StocktakeDetailPage/stocktakeDetailFunctions.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {
  Count,
  IfPlural,
  IfSingle,
  PluralBlock,
} from '../../../common/components/Plural.js'
import Checkbox from '../../../common/components/Checkbox.js'
import TextArea from '../../../common/components/TextArea.js'
import {
  updateStocktakeStatus,
  updateStocktakeStatusParams,
} from './UpdateStocktakeStatusModal.js'
import {STOCKTAKE_COMPLETED_DISPLAY} from '../../../common/constants/Stocktakes.js'

const MODAL_FORM = 'stocktake_reconcile_modal'

export async function showStocktakeReconcileModal({
  referenceID,
  skus = [],
  lineQueryParams = null,
  lineCount = 0,
}) {
  setForm(MODAL_FORM, {
    referenceID,
    lineQueryParams:
      skus.length === 0 && lineQueryParams
        ? omit(lineQueryParams, ['limit', 'offset'])
        : null,
    skus,
    lineCount,
    comment: '',
    markAsFinished: false,
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export async function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

async function getStocktakeLine(referenceID, sku) {
  const {json} = await apiverson.get(
    buildPath(['stocktake', referenceID, 'line'], {
      sku,
      limit: 1,
    }),
  )

  return json.stocktake_line[0]
}

async function reconcileStocktake() {
  try {
    let {referenceID, skus, lineQueryParams, comment, markAsFinished} =
      modalFormSelector(getState())

    updateModalForm({isSaving: true, serverError: null})

    if (skus.length === 1) {
      const line = await getStocktakeLine(referenceID, skus[0])

      if (line) {
        await apiverson.post(
          buildPath(['stocktake', referenceID, 'line', line.id, 'reconcile']),
          {
            comment: comment || undefined,
          },
        )
      } else {
        throw new Error('line not found')
      }
    } else {
      await apiverson.post(
        buildPath(['stocktake', referenceID, 'reconcile']),
        skus.length
          ? {criteria: {sku: skus}}
          : {criteria: lineQueryParams || {}},
      )
    }

    if (markAsFinished) {
      const params = updateStocktakeStatusParams(STOCKTAKE_COMPLETED_DISPLAY)
      await updateStocktakeStatus(referenceID, params)
    }

    if (currentPageSelector(getState()) === STOCKTAKE_LIST_PAGE) {
      await refreshStocktakeList()
    } else {
      await refreshStocktakeDetails()
    }

    showMessageToast('SKUs have been reconciled.')

    closeModal()
  } catch (err) {
    updateModalForm({
      serverError: `Error reconciling stocktake: ${
        err.message || err.error_message
      }`,
      isSaving: false,
    })
  }
}

function StocktakeReconcileModal({form}) {
  const totalToBeReconciled = form.skus.length + form.lineCount

  return (
    <ConfirmModal
      title="Reconcile Stocktake"
      onConfirm={() => reconcileStocktake()}
      onCancel={() => closeModal()}
      confirmText="Reconcile"
      cancelText="Close"
      isDisabled={totalToBeReconciled === 0}
      isSaving={form.isSaving}
      error={form.serverError}
    >
      {totalToBeReconciled === 0 ? (
        <div className="divider--bottom margin-bottom-20">
          <p className="fs-01 lh-md margin-bottom-0">
            <strong>No SKUs can be reconciled.</strong>
          </p>
          <p className="fs-n1 lh-md margin-top-5 margin-bottom-0">
            Refilter and try again.
          </p>
        </div>
      ) : (
        <>
          {form.skus.length > 0 ? (
            <div className="divider--bottom margin-bottom-30">
              <PluralBlock count={totalToBeReconciled}>
                <p className="fs-01 lh-md margin-bottom-15">
                  <strong>
                    <IfPlural>
                      <Count /> selected SKUs
                    </IfPlural>
                    <IfSingle>The selected SKU</IfSingle> will be reconciled in
                    this stocktake.
                  </strong>
                </p>
              </PluralBlock>
            </div>
          ) : form.lineQueryParams ? (
            <PluralBlock count={totalToBeReconciled}>
              <div className="divider--bottom margin-bottom-20">
                <p className="fs-01 lh-md margin-bottom-0">
                  <strong>
                    Are you sure you want to reconcile all filtered SKUs (
                    <Count />
                    )?
                  </strong>
                </p>
              </div>
            </PluralBlock>
          ) : (
            <PluralBlock count={totalToBeReconciled}>
              <div className="divider--bottom margin-bottom-20">
                <p className="fs-01 lh-md margin-bottom-0">
                  <strong>
                    Are you sure you want to reconcile all SKUs (<Count />) in
                    this stocktake?
                  </strong>
                </p>
              </div>
            </PluralBlock>
          )}
          <div className="row">
            <div className="medium-9 columns">
              <ul className="list list--no-style margin-bottom-20">
                {form.skus.length === 1 && (
                  <li className="list__item margin-bottom-30">
                    <TextArea
                      className="fs-n0 lh-lg"
                      label="Add an Explanation"
                      id="comment"
                      value={form.comment}
                      onChange={(comment) => updateModalForm({comment})}
                    />
                  </li>
                )}
                <li className="list__item margin-bottom-0">
                  <Checkbox
                    id="get_expected_qty"
                    label="Mark the Stocktake as Finished"
                    checked={form.markAsFinished}
                    onChange={(markAsFinished) =>
                      updateModalForm({markAsFinished})
                    }
                  />
                </li>
              </ul>
            </div>
          </div>
        </>
      )}
    </ConfirmModal>
  )
}

export default onlyIfForm(StocktakeReconcileModal, modalFormSelector)
